import { overflow } from "@prismicio/editor-support/String"
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  ErrorBoundary,
  Icon,
  Text,
} from "@prismicio/editor-ui"
import { type ReactNode, Suspense, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"

import { useRepositoryReleases } from "../../document/documentRouting"
import { CreateReleaseDialog } from "./CreateReleaseDialog"

interface ReleaseDropdownMenuProps {
  children?: ReactNode
}

export function ReleasesDropdownMenu(props: ReleaseDropdownMenuProps) {
  const { children } = props

  return (
    <Box alignItems="center" gap={4}>
      {children}
      <ErrorBoundary renderError={() => <ReleaseDropdownMenuTriggerFallback />}>
        <Suspense fallback={<ReleaseDropdownMenuTriggerFallback />}>
          <StopPropagation>
            <ReleasesDropdownMenuContent />
          </StopPropagation>
        </Suspense>
      </ErrorBoundary>
    </Box>
  )
}

export function ReleasesDropdownMenuContent() {
  const [open, setOpen] = useState(false)

  const { search } = useLocation()
  const navigate = useNavigate()
  const releases = useRepositoryReleases()

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger>
        <Box>
          <Icon name="arrowDropDown" size="medium" color="grey11" />
        </Box>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" maxHeight={256} minWidth={256}>
        <DropdownMenuLabel>
          <Text color="grey11">Releases</Text>
        </DropdownMenuLabel>

        {releases.map((release) => (
          <Link
            key={release.id}
            to={`/upcoming/${release.id}${search}`}
            style={{ textDecoration: "none" }}
          >
            <DropdownMenuItem
              description={release.timestamp ? "scheduled" : "unscheduled"}
            >
              <Box flexDirection="column">
                <Text underline={false} color="grey12">
                  {overflow(release.label ?? "...", 20)}
                </Text>
              </Box>
            </DropdownMenuItem>
          </Link>
        ))}

        {releases.length === 0 && (
          <DropdownMenuItem>
            <Text color="grey11" variant="smallItalic">
              No planned releases
            </Text>
          </DropdownMenuItem>
        )}

        <CreateReleaseDialog
          onSuccess={(release) => {
            navigate(`/upcoming/${release.id}${search}`)
            setOpen(false)
          }}
        >
          <DropdownMenuItem
            startIcon={<Icon name="add" />}
            onSelect={(event) => event.preventDefault()}
          >
            Add Release
          </DropdownMenuItem>
        </CreateReleaseDialog>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ReleaseDropdownMenuTriggerFallback() {
  return <Icon name="arrowDropDown" size="medium" color="grey5" />
}

interface StopPropagationProps {
  children: ReactNode
}

/**
 * @private
 * Since the trigger will be placed within the tabs of the header, we need to
 * avoid the click event from propagating to the tabs and changing the active
 * tab. The dropdown should be operable without changing the active tab.
 */
function StopPropagation(props: StopPropagationProps) {
  const { children } = props

  return (
    <Button
      invisible
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
    >
      {children}
    </Button>
  )
}
